/**
 * system_makeup — WHAT each star system is made of (2026-10-07). PURE and deterministic: the same system id gives the
 * same star, worlds, moons, comets and sky on every client, with no server state.
 *
 * THE SPACING LAW ("space things out so they are not everywhere"). Rare things are not rolled per system — a roll puts
 * two pulsars next door and none for a hundred light-hours. They are DEALT: every showpiece (SHOWPIECES: a pulsar, a
 * Wolf–Rayet pinwheel, a symbiotic nova, a contact binary …) exists a fixed number of times in the charted galaxy, and
 * each is seated on the system of its size class that lies FARTHEST from every showpiece already seated. Sky clusters
 * and rogue planets are dealt the same way. Everything else an ordinary system has is drawn from a small budget
 * (FEATURE_BUDGET): most systems have a star of a common type, typed planets, a few moons and at most two extras.
 *
 * THE LAYOUT IS NOT TOUCHED. Planet orbits, belts, dens, the station and the arrival lane come from shared/galaxy.ts
 * (mirrored on the server). A moon, a comet's whole orbit, a trojan swarm or a dwarf planet is kept clear of all of
 * them here, or it is not placed.
 */
import { prebuiltSystems, systemSector, systemNavPoints, type StarSystem, type SystemSector } from '../../galaxy.js';
import { derivedRingFor } from '../rings.js';
import { orbitPoint, type V3 } from './natural_orbit.js';
import {
  STELLAR_REACH,
  type StarKind, type SystemConfig, type SystemAge, type SystemMakeup, type NaturalStar, type PlanetMakeup, type PlanetKind,
  type MoonMakeup, type MoonKind, type FieldMakeup, type FieldTaxonomy, type FeatureMakeup, type OrbitMakeup, type SkyMakeup,
  type SkyClusterKind, type SkyNebulaKind,
} from './natural_types.js';

function fnv(s: string): number { let h = 0x811c9dc5; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); } return h >>> 0; }
/** fnv + murmur3's fmix32: bare FNV-1a is arithmetic in its last character (THE HASH LAW). */
function mix(s: string): number {
  let h = fnv(s);
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
const mix01 = (s: string): number => mix(s) / 4294967296;
const pick = <T>(list: ReadonlyArray<readonly [T, number]>, u: number): T => {
  let total = 0; for (const [, w] of list) total += w;
  let t = u * total;
  for (const [v, w] of list) { if ((t -= w) < 0) return v; }
  return list[list.length - 1][0];
};

// ── the showpieces ──────────────────────────────────────────────────────────────────────────────────────────────
export interface Showpiece {
  key: string;
  /** The layout class it needs (shared/galaxy StarSystem.starClass: the footprint is 70 / 130 / 220 km). */
  cls: 'dwarf' | 'main' | 'giant';
  primary: StarKind;
  companion?: StarKind;
  distant?: StarKind;
  config: SystemConfig;
  age: SystemAge;
  /** Its distant nebula (undefined = the older rule decides). */
  nebula?: SkyNebulaKind | null;
  /** How many exist among the charted systems. */
  count: number;
}
/** In dealing order: the rarest first, so they get the widest spacing. */
export const SHOWPIECES: ReadonlyArray<Showpiece> = [
  { key: 'magnetar', cls: 'dwarf', primary: 'magnetar', config: 'single', age: 'dead', nebula: 'remnant', count: 1 },
  { key: 'wolf_rayet_pinwheel', cls: 'giant', primary: 'wolf_rayet', companion: 'ob_star', config: 'colliding_wind', age: 'young', count: 1 },
  { key: 'xray_binary_bh', cls: 'main', primary: 'k_dwarf', companion: 'black_hole', config: 'xray_binary', age: 'old', nebula: null, count: 1 },
  { key: 'pulsar_nebula', cls: 'dwarf', primary: 'pulsar', config: 'single', age: 'dead', nebula: 'remnant', count: 1 },
  { key: 'lbv', cls: 'giant', primary: 'lbv', config: 'single', age: 'young', count: 1 },
  { key: 'symbiotic', cls: 'giant', primary: 'red_giant', companion: 'white_dwarf', config: 'symbiotic', age: 'old', count: 1 },
  { key: 'protostar', cls: 'main', primary: 'protostar', config: 'single', age: 'forming', nebula: 'dark', count: 1 },
  { key: 'pulsar_runaway', cls: 'dwarf', primary: 'pulsar', config: 'single', age: 'dead', nebula: null, count: 1 },
  { key: 'wolf_rayet', cls: 'giant', primary: 'wolf_rayet', config: 'single', age: 'young', count: 1 },
  { key: 'xray_binary_ns', cls: 'main', primary: 'g_dwarf', companion: 'neutron_star', config: 'xray_binary', age: 'old', nebula: null, count: 1 },
  { key: 'cataclysmic', cls: 'dwarf', primary: 'k_dwarf', companion: 'white_dwarf', config: 'semidetached', age: 'old', count: 1 },
  { key: 'post_agb', cls: 'giant', primary: 'post_agb', config: 'single', age: 'old', nebula: null, count: 1 },
  { key: 'be_xray', cls: 'main', primary: 'be_star', companion: 'neutron_star', config: 'be_xray', age: 'young', count: 1 },
  { key: 'wd_debris', cls: 'dwarf', primary: 'white_dwarf', config: 'wd_debris', age: 'dead', nebula: 'planetary', count: 1 },
  { key: 'young_binary', cls: 'main', primary: 't_tauri', companion: 't_tauri', config: 'young_binary', age: 'forming', nebula: 'emission', count: 1 },
  { key: 'red_supergiant', cls: 'giant', primary: 'red_supergiant', config: 'single', age: 'old', count: 2 },
  { key: 'contact_binary', cls: 'dwarf', primary: 'k_dwarf', companion: 'k_dwarf', config: 'contact_binary', age: 'mature', count: 1 },
  { key: 'herbig', cls: 'main', primary: 'herbig', config: 'single', age: 'young', nebula: 'reflection', count: 1 },
  { key: 'blue_supergiant', cls: 'giant', primary: 'blue_supergiant', config: 'single', age: 'young', count: 2 },
  { key: 'neutron_star', cls: 'dwarf', primary: 'neutron_star', config: 'single', age: 'dead', nebula: null, count: 1 },
  { key: 'hierarchical_triple', cls: 'main', primary: 'g_dwarf', companion: 'k_dwarf', distant: 'm_dwarf', config: 'hierarchical_triple', age: 'mature', count: 1 },
  { key: 'carbon_star', cls: 'giant', primary: 'carbon_star', config: 'single', age: 'old', count: 1 },
  { key: 'white_dwarf', cls: 'dwarf', primary: 'white_dwarf', config: 'single', age: 'dead', count: 2 },
  { key: 'be_star', cls: 'main', primary: 'be_star', config: 'single', age: 'young', count: 1 },
  { key: 'agb', cls: 'giant', primary: 'agb', config: 'single', age: 'old', count: 2 },
  { key: 't_tauri', cls: 'dwarf', primary: 't_tauri', config: 'single', age: 'young', nebula: 'dark', count: 1 },
  { key: 'ob_star', cls: 'giant', primary: 'ob_star', config: 'single', age: 'young', nebula: 'emission', count: 3 },
  { key: 'detached_binary', cls: 'main', primary: 'g_dwarf', companion: 'k_dwarf', config: 'detached_binary', age: 'mature', count: 3 },
  { key: 'mira', cls: 'giant', primary: 'mira', config: 'single', age: 'old', count: 2 },
  { key: 'brown_dwarf', cls: 'dwarf', primary: 'brown_dwarf', config: 'single', age: 'old', count: 2 },
  { key: 'cepheid', cls: 'giant', primary: 'cepheid', config: 'single', age: 'mature', count: 2 },
  { key: 'wide_binary', cls: 'main', primary: 'g_dwarf', distant: 'k_dwarf', config: 'wide_binary', age: 'mature', count: 3 },
  { key: 'flare_star', cls: 'dwarf', primary: 'm_dwarf', config: 'single', age: 'mature', count: 3 },
];
/** The pairs a detached binary may be (hotter first). */
const DETACHED_PAIRS: ReadonlyArray<readonly [StarKind, StarKind]> = [['g_dwarf', 'k_dwarf'], ['f_dwarf', 'g_dwarf'], ['a_star', 'f_dwarf'], ['k_dwarf', 'm_dwarf']];
/** An ordinary system's star, by its layout class. */
const COMMON: Readonly<Record<'dwarf' | 'main' | 'giant', ReadonlyArray<readonly [StarKind, number]>>> = {
  dwarf: [['m_dwarf', 0.7], ['k_dwarf', 0.3]],
  main: [['g_dwarf', 0.32], ['k_dwarf', 0.26], ['f_dwarf', 0.24], ['a_star', 0.18]],
  giant: [['red_giant', 1]],
};
/** Sky clusters dealt among the charted systems (kind, count). */
export const SKY_CLUSTERS: ReadonlyArray<readonly [SkyClusterKind, number]> = [['globular_cluster', 3], ['open_cluster', 4], ['association', 2], ['young_cluster', 1], ['embedded_cluster', 1]];
/** Rogue planets dealt among the ordinary systems. */
export const ROGUE_COUNT = 3;
/** Interstellar visitors (a body from another star passing through) dealt among the ordinary systems. */
export const VISITOR_COUNT = 2;
/** Extras an ordinary system may have (a showpiece system: one fewer). */
export const FEATURE_BUDGET = 2;
/** Moons in one system, at most. */
export const MOON_CAP = 4;
/**
 * THE CATCHABLE CLOCK. Everything orbits on the wall clock, slowly enough that a ship at cruise (105 m/s) can fly to a
 * body and keep station with it: the innermost moon takes 4 h (under 105 m/s round the largest planet), a comet 6–12 h (a few
 * hundred m/s at its fastest), a centaur 10–20 h. The first cut's minutes-long orbits were km/s — a moon no ship could reach.
 */
export const MOON_PERIOD_S = 14_400;

/** Starlight relative to a G dwarf (the planets' heat; the key light's own scale is the star layer's business). */
export const STAR_HEAT: Readonly<Record<StarKind, number>> = {
  m_dwarf: 0.12, k_dwarf: 0.45, g_dwarf: 1, f_dwarf: 2.2, a_star: 6, ob_star: 30,
  red_giant: 14, agb: 22, post_agb: 12, carbon_star: 20, mira: 18, cepheid: 26,
  red_supergiant: 60, blue_supergiant: 80, lbv: 120, wolf_rayet: 90, be_star: 18,
  protostar: 0.6, t_tauri: 0.9, herbig: 8,
  brown_dwarf: 0.004, white_dwarf: 0.02, neutron_star: 0.002, pulsar: 0.002, magnetar: 0.002, black_hole: 0,
};
/** The distance (m) at which STAR_HEAT is the heat. */
export const HEAT_REF_M = 600_000;

const galDist = (a: StarSystem, b: StarSystem): number => Math.hypot(a.gx - b.gx, a.gy - b.gy, a.gz - b.gz);

/** Of `pool`, the system farthest from every host (the first by its hash when there is no host yet). */
function farthest(pool: StarSystem[], hosts: StarSystem[], salt: string): StarSystem | null {
  let best: StarSystem | null = null, bestScore = -1;
  for (const s of pool) {
    let d = Infinity;
    for (const h of hosts) d = Math.min(d, galDist(s, h));
    const score = (Number.isFinite(d) ? d : 0) + mix01(`${s.id}:${salt}`) * 1e-3;
    if (score > bestScore) { bestScore = score; best = s; }
  }
  return best;
}

interface Dealt {
  showpiece: Map<string, Showpiece>;
  sky: Map<string, SkyClusterKind>;
  rogue: Set<string>;
  visitor: Set<string>;
}
let dealtMemo: Dealt | null = null;
/** The deal over the 100 charted systems (sys0 is authored and takes no part). */
function deal(): Dealt {
  if (dealtMemo) return dealtMemo;
  const all = prebuiltSystems().filter((s) => s.id !== 'sys0');
  const showpiece = new Map<string, Showpiece>();
  const hosts: StarSystem[] = [];
  for (const sp of SHOWPIECES) {
    for (let n = 0; n < sp.count; n++) {
      const pool = all.filter((s) => s.starClass === sp.cls && !showpiece.has(s.id));
      const host = farthest(pool, hosts, `sp:${sp.key}:${n}`);
      if (!host) break; // the class is full: the later, commoner showpieces give way
      showpiece.set(host.id, sp); hosts.push(host);
    }
  }
  const sky = new Map<string, SkyClusterKind>();
  const skyHosts: StarSystem[] = [];
  for (const [kind, count] of SKY_CLUSTERS) {
    for (let n = 0; n < count; n++) {
      const host = farthest(all.filter((s) => !sky.has(s.id)), skyHosts, `sky:${kind}:${n}`);
      if (!host) break;
      sky.set(host.id, kind); skyHosts.push(host);
    }
  }
  const rogue = new Set<string>();
  const rogueHosts: StarSystem[] = [];
  for (let n = 0; n < ROGUE_COUNT; n++) {
    const host = farthest(all.filter((s) => !showpiece.has(s.id) && !rogue.has(s.id)), rogueHosts, `rogue:${n}`);
    if (!host) break;
    rogue.add(host.id); rogueHosts.push(host);
  }
  const visitor = new Set<string>();
  const visitorHosts: StarSystem[] = [];
  for (let n = 0; n < VISITOR_COUNT; n++) {
    const host = farthest(all.filter((s) => !showpiece.has(s.id) && !rogue.has(s.id) && !visitor.has(s.id)), visitorHosts, `visitor:${n}`);
    if (!host) break;
    visitor.add(host.id); visitorHosts.push(host);
  }
  dealtMemo = { showpiece, sky, rogue, visitor };
  return dealtMemo;
}
/** The charted system that hosts a showpiece (the n-th of its kind), or null. */
export function hostOf(key: string, n = 0): string | null {
  let k = 0;
  for (const [id, sp] of deal().showpiece) if (sp.key === key && k++ === n) return id;
  return null;
}
/** Which showpiece each charted system hosts (system id → key). */
export function showpieceHosts(): Map<string, string> {
  const out = new Map<string, string>();
  for (const [id, sp] of deal().showpiece) out.set(id, sp.key);
  return out;
}

// ── clearances ──────────────────────────────────────────────────────────────────────────────────────────────────
interface Body { x: number; y: number; z: number; r: number }
/** Everything the layout owns: the stellar footprint, the arrival point, planets, belts, dens, the station. */
function layoutBodies(sys: StarSystem, sec: SystemSector): Body[] {
  const out: Body[] = [{ x: 0, y: 0, z: 0, r: sec.star.radius * STELLAR_REACH }, { x: sec.star.radius * 2.4, y: 0, z: 0, r: 30_000 }];
  for (const p of systemNavPoints(sys)) out.push({ x: p.pos[0], y: p.pos[1], z: p.pos[2], r: p.radius });
  return out;
}
const gapTo = (b: Body, x: number, y: number, z: number): number => Math.hypot(b.x - x, b.y - y, b.z - z) - b.r;
function clearOf(bodies: Body[], x: number, y: number, z: number, margin: number, skip = -1): boolean {
  for (let i = 0; i < bodies.length; i++) if (i !== skip && gapTo(bodies[i], x, y, z) < margin) return false;
  return true;
}
/** The whole orbit keeps `margin` clear of every layout body. */
export function orbitClear(o: OrbitMakeup, bodies: ReadonlyArray<{ x: number; y: number; z: number; r: number }>, margin: number): boolean {
  const p: V3 = [0, 0, 0];
  for (let i = 0; i < 128; i++) {
    orbitPoint(o, (i / 128) * Math.PI * 2, p);
    for (const b of bodies) if (Math.hypot(b.x - p[0], b.y - p[1], b.z - p[2]) - b.r < margin) return false;
  }
  return true;
}

// ── planets and moons ───────────────────────────────────────────────────────────────────────────────────────────
function planetKindOf(face: number, heat: number, radius: number, u: number): PlanetKind {
  if (face === 1) { // the gas face
    if (heat > 14) return 'ultra_hot_giant';
    if (heat > 4) return 'hot_giant';
    if (radius < 38_000) return u < 0.22 ? 'super_puff' : 'sub_neptune';
    return heat < 0.15 ? 'ice_giant' : 'gas_giant';
  }
  if (face === 0) return heat > 6 ? 'lava' : heat > 2.2 ? (u < 0.6 ? 'greenhouse' : 'airless') : 'desert';
  if (face === 2) return heat > 1 ? 'water' : 'frozen';
  if (face === 3) return heat > 6 ? 'lava' : 'airless';
  return heat > 2 ? 'greenhouse' : heat >= 0.3 ? 'temperate' : u < 0.5 ? 'frozen' : 'water'; // the ocean face
}
const GIANTS: ReadonlySet<PlanetKind> = new Set<PlanetKind>(['gas_giant', 'ice_giant', 'sub_neptune', 'super_puff', 'hot_giant', 'ultra_hot_giant']);

/** The orbit plane (inclination about x, node about y — natural_orbit.moonOffsetAt) whose normal is `n`. */
export function planeOf(n: { x: number; y: number; z: number }): { inc: number; node: number } {
  const s = (n.y < 0 ? -1 : 1) / (Math.hypot(n.x, n.y, n.z) || 1), nx = n.x * s, ny = n.y * s, nz = n.z * s;
  const inc = Math.acos(Math.max(-1, Math.min(1, ny)));
  return { inc, node: Math.sin(inc) < 1e-6 ? 0 : Math.atan2(nx, -nz) };
}

function moonsFor(id: string, idx: number, kind: PlanetKind, radius: number, maxOrbit: number, left: number, young: boolean, ring: { rOut: number; normal: { x: number; y: number; z: number } } | null): MoonMakeup[] {
  if (left <= 0 || maxOrbit < radius * 2.5 || kind === 'hot_giant' || kind === 'ultra_hot_giant' || kind === 'lava') return [];
  const ringPlane = ring ? planeOf(ring.normal) : null;
  const big = kind === 'gas_giant' || kind === 'ice_giant';
  const u = mix01(`${id}:mn${idx}`);
  const want = big ? (u < 0.25 ? 1 : u < 0.7 ? 2 : 3) : GIANTS.has(kind) ? (u < 0.4 ? 1 : 0) : (u < 0.28 ? 1 : 0);
  const out: MoonMakeup[] = [];
  // a ringed planet's regular moons orbit in the ring's plane, outside it
  let orbit = radius * Math.max(2.6 + mix01(`${id}:mo${idx}`) * 0.5, ring ? ring.rOut * 1.14 : 0);
  for (let k = 0; k < Math.min(want, left) && orbit <= maxOrbit; k++) {
    const key = `${id}:m${idx}:${k}`;
    const outermost = k === want - 1 && want > 1;
    let mk: MoonKind;
    if (!big) mk = mix01(`${key}:k`) < 0.7 ? 'rocky' : 'irregular';
    else if (k === 0 && kind === 'gas_giant' && mix01(`${key}:k`) < 0.45) mk = 'volcanic';
    else if (outermost && mix01(`${key}:k`) < 0.5) mk = 'irregular';
    else mk = pick<MoonKind>([['icy_ocean', 0.42], ['plume', 0.22], ['hazy', 0.16], ['rocky', 0.2]], mix01(`${key}:k2`));
    const irregular = mk === 'irregular';
    out.push({
      kind: mk, seed: mix(`${key}:s`) % 100_000,
      orbitM: orbit,
      inc: irregular ? 0.35 + mix01(`${key}:i`) * 0.5 : ringPlane ? ringPlane.inc : (mix01(`${key}:i`) - 0.5) * 0.16,
      node: !irregular && ringPlane ? ringPlane.node : mix01(`${key}:n`) * Math.PI * 2,
      // Kepler's third law about one planet: the inner moon laps the outer (a resonant chain of 1 : 2 : 4 when it fits)
      periodS: Math.round(MOON_PERIOD_S * Math.pow(orbit / (radius * 2.6), 1.5)), phase0: mix01(`${key}:p`) * Math.PI * 2, sense: irregular && mix01(`${key}:r`) < 0.6 ? -1 : 1,
      radiusM: radius * (irregular ? 0.035 + mix01(`${key}:z`) * 0.03 : mk === 'hazy' ? 0.2 + mix01(`${key}:z`) * 0.06 : 0.09 + mix01(`${key}:z`) * 0.11),
    });
    orbit *= young ? 1.7 : Math.pow(2, 2 / 3); // 2 : 1 with the one inside it
  }
  return out;
}

// ── the makeup ──────────────────────────────────────────────────────────────────────────────────────────────────
const memo = new Map<string, SystemMakeup>();

/** What `sys` is made of. Deterministic in the system alone (and, for a charted system, the charted set). */
export function systemMakeup(sys: StarSystem): SystemMakeup {
  const hit = memo.get(sys.id);
  if (hit) return hit;
  const id = sys.id;
  const sec = systemSector(sys);
  const star = (kind: StarKind, tag: string): NaturalStar => ({ kind, seed: mix(`${id}:star:${tag}`) % 100_000 });
  if (id === 'sys0') {
    // The Commons Reach is authored: a G dwarf, its planets and belts as they stand; its two comets become real ones.
    const mk: SystemMakeup = {
      systemId: id, showpiece: '', age: 'mature', footprintM: sec.star.radius, config: 'single', primary: star('g_dwarf', 'a'), companion: null, distant: null,
      planets: [], fields: [],
      features: [
        { kind: 'comet', variant: 'active', seed: 11, orbit: null, fixed: [150_000, 420_000, 640_000], nucleusM: 900 },
        { kind: 'comet', variant: 'active', seed: 29, orbit: null, fixed: [-1_150_000, -420_000, -700_000], nucleusM: 1_100 },
      ],
      sky: [{ kind: 'open_cluster', seed: 7, dir: norm([-0.35, 0.62, -0.7]), angR: 0.07 }],
    };
    memo.set(id, mk);
    return mk;
  }
  const charted = !id.startsWith('w');
  const d = charted ? deal() : null;
  const sp = d?.showpiece.get(id) ?? null;
  let primaryKind: StarKind, companionKind: StarKind | null = null, distantKind: StarKind | null = null, config: SystemConfig = 'single', age: SystemAge;
  if (sp) {
    primaryKind = sp.primary; companionKind = sp.companion ?? null; distantKind = sp.distant ?? null; config = sp.config; age = sp.age;
    if (sp.key === 'detached_binary') [primaryKind, companionKind] = DETACHED_PAIRS[mix(`${id}:pair`) % DETACHED_PAIRS.length];
    if (sp.key === 'wide_binary') { primaryKind = pick(COMMON.main, mix01(`${id}:wp`)); distantKind = pick<StarKind>([['k_dwarf', 0.4], ['m_dwarf', 0.4], ['g_dwarf', 0.2]], mix01(`${id}:wd`)); }
  } else {
    primaryKind = pick(COMMON[sys.starClass], mix01(`${id}:kind`));
    age = sys.starClass === 'giant' ? 'old' : 'mature';
  }
  const young = age === 'young' || age === 'forming';
  // a gas disc survives only round a low-mass infant: a massive young star (O/B, a blue supergiant, a Wolf–Rayet) photo-evaporates it at once
  const discBearing = age === 'forming' || primaryKind === 't_tauri' || primaryKind === 'herbig';
  const heatOf = STAR_HEAT[primaryKind] + (companionKind ? STAR_HEAT[companionKind] : 0);
  const bodies = layoutBodies(sys, sec);

  // planets, in the sector's order
  const planets: PlanetMakeup[] = [];
  let moonsLeft = MOON_CAP;
  let shepherded = false;
  const order = sec.planets.map((_, i) => i).sort((a, b) => Math.hypot(...sec.planets[a].pos) - Math.hypot(...sec.planets[b].pos));
  const byIndex = new Map<number, PlanetMakeup>();
  for (const i of order) {
    const pl = sec.planets[i];
    const r = Math.hypot(pl.pos[0], pl.pos[1], pl.pos[2]);
    const heat = heatOf / Math.pow(r / HEAT_REF_M, 2);
    const kind = planetKindOf(pl.texIdx, heat, pl.radius, mix01(`${id}:pk${i}`));
    // the room a moon has: to the nearest other layout body (its own planet is the body named like it)
    let gap = Infinity;
    for (const b of bodies) { const g = gapTo(b, pl.pos[0], pl.pos[1], pl.pos[2]); if (g > -pl.radius * 0.5 && g < gap && !(Math.abs(b.x - pl.pos[0]) < 1 && Math.abs(b.z - pl.pos[2]) < 1)) gap = g; }
    const maxOrbit = Math.min(pl.radius * 6, (gap - pl.radius) * 0.42);
    // PL-24: the ring rule is rings.derivedRingFor's (a gas-faced planet, 60 % of them) — never round a scorched giant
    const ringSpec = kind === 'hot_giant' || kind === 'ultra_hot_giant' ? null : derivedRingFor(id, i, pl.texIdx, { x: pl.pos[0], y: pl.pos[1], z: pl.pos[2] });
    const moons = moonsFor(id, i, kind, pl.radius, maxOrbit, moonsLeft, discBearing, ringSpec);
    moonsLeft -= moons.length;
    // SY-05: a ringed giant keeps a shepherd — a tiny moon just outside the ring's edge, in its plane (one per system, outside the cap)
    if (ringSpec && !shepherded && pl.radius * ringSpec.rOut * 1.05 < maxOrbit && mix01(`${id}:sh${i}`) < 0.6) {
      shepherded = true;
      const pln = planeOf(ringSpec.normal), o = pl.radius * ringSpec.rOut * 1.035;
      moons.unshift({ kind: 'irregular', seed: mix(`${id}:shs${i}`) % 100_000, orbitM: o, inc: pln.inc, node: pln.node, periodS: Math.round(MOON_PERIOD_S * Math.pow(o / (pl.radius * 2.6), 1.5)), phase0: mix01(`${id}:shp${i}`) * Math.PI * 2, sense: 1, radiusM: pl.radius * (0.012 + mix01(`${id}:shr${i}`) * 0.008), shepherd: true });
    }
    const rocky = !GIANTS.has(kind);
    const p: PlanetMakeup = {
      index: i, kind, face: pl.texIdx, seed: mix(`${id}:pseed${i}`) % 100_000, heat,
      superEarth: rocky && pl.radius > 72_000,
      evaporating: (kind === 'hot_giant' && mix01(`${id}:ev${i}`) < 0.2) || (kind === 'ultra_hot_giant' && mix01(`${id}:ev${i}`) < 0.45) || (kind === 'lava' && heat > 14 && mix01(`${id}:ev${i}`) < 0.2),
      aurora: (kind === 'gas_giant' || kind === 'ice_giant') && (moons.some((m) => m.kind === 'volcanic') || mix01(`${id}:au${i}`) < 0.4),
      circumplanetary: false,
      ringed: !!ringSpec,
      moons,
    };
    byIndex.set(i, p);
  }
  for (let i = 0; i < sec.planets.length; i++) planets.push(byIndex.get(i)!);
  const orbitsM = sec.planets.map((p) => Math.hypot(p.pos[0], p.pos[1], p.pos[2])).sort((a, b) => a - b);
  const firstOrbit = orbitsM[0] ?? sec.star.radius * 3 + 150_000;
  const outerOrbit = orbitsM[orbitsM.length - 1] ?? firstOrbit;

  // belts
  const fields: FieldMakeup[] = [];
  const landmarkAt = mix01(`${id}:lm`) < 0.2 ? mix(`${id}:lmf`) % Math.max(1, sec.fields.length) : -1;
  sec.fields.forEach((f, i) => {
    const taxonomy: FieldTaxonomy = sys.tier >= 3 ? 'icy' : pick<FieldTaxonomy>([['carbonaceous', 0.45], ['stony', 0.4], ['metallic', 0.15]], mix01(`${id}:ft${i}`));
    let landmark: FieldMakeup['landmark'] = null;
    if (i === landmarkAt) {
      const a = mix01(`${id}:lma`) * Math.PI * 2, rr = f.radius * 1.35 + 3_000;
      const pos: V3 = [f.pos[0] + Math.cos(a) * rr, f.pos[1] + (mix01(`${id}:lmy`) - 0.5) * f.radius * 0.5, f.pos[2] + Math.sin(a) * rr];
      const radiusM = 420 + mix01(`${id}:lmr`) * 520;
      const skip = bodies.findIndex((b) => Math.abs(b.x - f.pos[0]) < 1 && Math.abs(b.z - f.pos[2]) < 1);
      if (clearOf(bodies, pos[0], pos[1], pos[2], radiusM + 2_500, skip)) {
        landmark = { kind: pick([['contact_binary', 0.34], ['binary_asteroid', 0.22], ['active_asteroid', 0.22], ['rubble_pile', 0.22]] as const, mix01(`${id}:lmk`)), pos, radiusM, seed: mix(`${id}:lms`) % 100_000 };
      }
    }
    fields.push({ index: i, taxonomy, structure: mix01(`${id}:fs${i}`) < 0.55 ? 'rubble' : 'coherent', landmark });
  });

  // extras, within the budget
  const features: FeatureMakeup[] = [];
  const edge = 2_250_000; // inside the sector's 2,400 km edge
  if (discBearing) {
    features.push({ kind: 'proto_disc', seed: mix(`${id}:pd`) % 100_000, innerM: sec.star.radius * 1.3, outerM: Math.min(edge, outerOrbit * 1.15 + 150_000), gapsM: orbitsM });
    // NE-09: the coolest giant keeps a disc of its own — an UNRINGED one when there is one. A giant never wears both: a
    // ring and a moon-forming disc are two flat things in one plane (the disc was drawn outside the ring at half depth).
    const giants = planets.filter((p) => p.kind === 'gas_giant' || p.kind === 'ice_giant').sort((a, b) => b.heat - a.heat);
    const gi = giants.filter((p) => !p.ringed).pop() ?? giants.pop();
    if (gi && mix01(`${id}:cpd`) < 0.6) {
      gi.circumplanetary = true;
      if (gi.ringed) { gi.ringed = false; gi.moons = gi.moons.filter((m) => !m.shepherd); } // no ring, no shepherd
    }
  }
  let budget = FEATURE_BUDGET - (sp ? 1 : 0);
  const comet = (tag: string, variant: 'active' | 'main_belt' | 'dormant'): FeatureMakeup | null => {
    const nucleusM = 500 + mix01(`${id}:${tag}:n`) * 900;
    for (let tryN = 0; tryN < 16; tryN++) {
      const k = `${id}:${tag}:${tryN}`;
      const beltR = sec.fields.length ? Math.hypot(sec.fields[0].pos[0], sec.fields[0].pos[2]) : firstOrbit * 0.7;
      const q = variant === 'main_belt' ? beltR * (0.92 + mix01(`${k}:q`) * 0.1) : sec.star.radius * 1.6 + 40_000 + mix01(`${k}:q`) * Math.max(20_000, firstOrbit * 0.8 - sec.star.radius * 1.6 - 40_000);
      const Q = variant === 'main_belt' ? q * (1.18 + mix01(`${k}:Q`) * 0.2) : 1_300_000 + mix01(`${k}:Q`) * 850_000;
      const inc = variant === 'main_belt' ? 0.14 + mix01(`${k}:i`) * 0.16 : (0.35 + mix01(`${k}:i`) * 0.8) * (mix01(`${k}:is`) < 0.5 ? 1 : -1);
      const orbit: OrbitMakeup = { q, Q, inc, node: mix01(`${k}:o`) * Math.PI * 2, arg: mix01(`${k}:w`) * Math.PI * 2, periodS: Math.round((variant === 'main_belt' ? 28_800 : 21_600) + mix01(`${id}:${tag}:T`) * 21_600), phase0: mix01(`${id}:${tag}:M`) * Math.PI * 2 };
      if (Q <= edge && orbitClear(orbit, bodies, nucleusM + 25_000)) return { kind: 'comet', variant, seed: mix(`${id}:${tag}:s`) % 100_000, orbit, fixed: null, nucleusM };
    }
    return null;
  };
  type Cand = { p: number; make: () => FeatureMakeup | null };
  const gasIdx = planets.findIndex((p) => p.kind === 'gas_giant' || p.kind === 'ice_giant');
  const hotStar = primaryKind === 'a_star' || primaryKind === 'f_dwarf';
  const cands: Cand[] = charted ? [
    { p: 0.3, make: () => comet('c0', pick([['active', 0.7], ['main_belt', 0.15], ['dormant', 0.15]] as const, mix01(`${id}:cv`))) },
    { p: discBearing ? 0 : 0.15, make: () => ({ kind: 'dust_band', seed: mix(`${id}:db`) % 100_000, innerM: sec.star.radius * 1.5, outerM: firstOrbit * (1.1 + mix01(`${id}:dbo`) * 0.5), strength: 0.4 + mix01(`${id}:dbs`) * 0.6 }) },
    { p: discBearing ? 0 : hotStar ? 0.3 : young ? 0.2 : 0.08, make: () => { const innerM = outerOrbit * 1.12 + 60_000, outerM = innerM + 180_000 + mix01(`${id}:ddw`) * 180_000; return outerM <= edge ? { kind: 'debris_disc', seed: mix(`${id}:dd`) % 100_000, innerM, outerM } : null; } },
    { p: discBearing ? 0 : 0.14, make: () => {
      const radiusM = Math.max(outerOrbit + 260_000, 1_650_000) + mix01(`${id}:tr`) * 150_000;
      if (radiusM > 2_200_000) return null;
      let dwarf: { pos: V3; radiusM: number; moonRadiusM: number; moonOrbitM: number } | null = null;
      if (mix01(`${id}:tdw`) < 0.55) {
        const a = mix01(`${id}:tda`) * Math.PI * 2, rM = 9_000 + mix01(`${id}:tdr`) * 6_000;
        const pos: V3 = [Math.cos(a) * radiusM, (mix01(`${id}:tdy`) - 0.5) * 120_000, Math.sin(a) * radiusM];
        if (clearOf(bodies, pos[0], pos[1], pos[2], rM * 6)) dwarf = { pos, radiusM: rM, moonRadiusM: rM * (0.45 + mix01(`${id}:tdm`) * 0.1), moonOrbitM: rM * (3.2 + mix01(`${id}:tdo`) * 1.3) };
      }
      return { kind: 'tno_belt', seed: mix(`${id}:tno`) % 100_000, radiusM, widthM: 120_000 + mix01(`${id}:tw`) * 100_000, count: 40 + (mix(`${id}:tc`) % 31), dwarf };
    } },
    { p: gasIdx >= 0 ? 0.22 : 0, make: () => {
      const pl = sec.planets[gasIdx], r = Math.hypot(pl.pos[0], pl.pos[2]), a0 = Math.atan2(pl.pos[2], pl.pos[0]);
      const swarms: Array<{ pos: V3; radiusM: number; count: number }> = [];
      for (const s of [1, -1]) {
        const a = a0 + (s * Math.PI) / 3, radiusM = 14_000 + mix01(`${id}:tj${s}`) * 8_000;
        const pos: V3 = [Math.cos(a) * r, pl.pos[1], Math.sin(a) * r];
        if (clearOf(bodies, pos[0], pos[1], pos[2], radiusM + 40_000)) swarms.push({ pos, radiusM, count: 14 + (mix(`${id}:tjc${s}`) % 11) });
      }
      return swarms.length ? { kind: 'trojans', seed: mix(`${id}:tj`) % 100_000, planet: gasIdx, swarms } : null;
    } },
    { p: sec.planets.length >= 2 ? 0.08 : 0, make: () => {
      const k = mix(`${id}:cen`) % (orbitsM.length - 1), mid = (orbitsM[k] + orbitsM[k + 1]) / 2, e = 0.05 + mix01(`${id}:cene`) * 0.1;
      for (let tryN = 0; tryN < 12; tryN++) {
        const orbit: OrbitMakeup = { q: mid * (1 - e), Q: mid * (1 + e), inc: 0.12 + mix01(`${id}:ceni${tryN}`) * 0.2, node: mix01(`${id}:ceno${tryN}`) * Math.PI * 2, arg: mix01(`${id}:cenw`) * Math.PI * 2, periodS: 36_000 + (mix(`${id}:cenT`) % 36_000), phase0: mix01(`${id}:cenM`) * Math.PI * 2 };
        if (orbit.Q <= edge && orbitClear(orbit, bodies, 30_000)) return { kind: 'centaur', seed: mix(`${id}:cens`) % 100_000, orbit, radiusM: 1_400 + mix01(`${id}:cenr`) * 1_800, active: mix01(`${id}:cena`) < 0.35 };
      }
      return null;
    } },
  ] : [
    { p: 0.2, make: () => comet('c0', 'active') },
    { p: 0.1, make: () => ({ kind: 'dust_band', seed: mix(`${id}:db`) % 100_000, innerM: sec.star.radius * 1.5, outerM: firstOrbit * 1.3, strength: 0.5 }) },
  ];
  // a fixed order per system: the candidates take their turns by hash, and the budget ends the round
  const turn = cands.map((c, i) => ({ c, i })).sort((a, b) => mix01(`${id}:turn${a.i}`) - mix01(`${id}:turn${b.i}`));
  for (const { c, i } of turn) {
    if (budget <= 0) break;
    if (mix01(`${id}:roll${i}`) >= c.p) continue;
    const f = c.make();
    if (!f) continue;
    features.push(f); budget--;
    // a shedding comet leaves its pebbles on its own path (they share its place in the budget)
    if (f.kind === 'comet' && f.variant === 'active' && f.orbit && mix01(`${id}:ms`) < 0.35) features.push({ kind: 'meteoroid_stream', seed: mix(`${id}:mss`) % 100_000, orbit: f.orbit, count: 60 + (mix(`${id}:msc`) % 61) });
  }
  if (d?.rogue.has(id)) {
    const az = mix01(`${id}:rga`) * Math.PI * 2, el = (0.3 + mix01(`${id}:rge`) * 0.6) * (mix01(`${id}:rgs`) < 0.5 ? 1 : -1), dist = 1_900_000 + mix01(`${id}:rgd`) * 350_000;
    features.push({ kind: 'rogue', seed: mix(`${id}:rg`) % 100_000, pos: [Math.cos(az) * Math.cos(el) * dist, Math.sin(el) * dist, Math.sin(az) * Math.cos(el) * dist], radiusM: 30_000 + mix01(`${id}:rgr`) * 25_000 });
  }

  if (d?.visitor.has(id)) {
    // SB-13: a straight line through the system, well inclined, missing the star by at least the first orbit's half
    for (let tryN = 0; tryN < 24; tryN++) {
      const k = `${id}:iv${tryN}`;
      const az = mix01(`${k}:a`) * Math.PI * 2, el = (0.25 + mix01(`${k}:e`) * 0.5) * (mix01(`${k}:s`) < 0.5 ? 1 : -1);
      const dir: V3 = [Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el)];
      const side = norm([-dir[2], 0.3 * (mix01(`${k}:u`) - 0.5), dir[0]]);
      const miss = Math.max(firstOrbit * 0.5, sec.star.radius * 2) + mix01(`${k}:m`) * 400_000;
      const half = Math.sqrt(Math.max(1, edge * edge - miss * miss));
      const from: V3 = [side[0] * miss - dir[0] * half, side[1] * miss - dir[1] * half, side[2] * miss - dir[2] * half];
      const to: V3 = [side[0] * miss + dir[0] * half, side[1] * miss + dir[1] * half, side[2] * miss + dir[2] * half];
      let ok = true;
      for (let j = 0; j <= 96 && ok; j++) { const t = j / 96; ok = clearOf(bodies, from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t, from[2] + (to[2] - from[2]) * t, 30_000); }
      if (!ok) continue;
      features.push({ kind: 'interstellar', seed: mix(`${id}:ivs`) % 100_000, from, to, periodS: 9_000 + (mix(`${id}:ivT`) % 5_400), phase0: mix01(`${id}:ivp`), lengthM: 500 + mix01(`${id}:ivl`) * 700 });
      break;
    }
  }

  // the sky
  const sky: SkyMakeup[] = [];
  const skyKind = d?.sky.get(id);
  if (skyKind) {
    const az = mix01(`${id}:ska`) * Math.PI * 2, el = (mix01(`${id}:ske`) - 0.5) * 1.6;
    sky.push({
      kind: skyKind, seed: mix(`${id}:sks`) % 100_000, dir: [Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el)],
      // landmarks, not catalogue entries: a few degrees across in a sky where a planet fills tens (the first cut, at true scale, drew a globular as one bright dot)
      angR: (skyKind === 'globular_cluster' ? 0.05 : skyKind === 'open_cluster' ? 0.075 : skyKind === 'young_cluster' ? 0.03 : skyKind === 'association' ? 0.16 : 0.05) * (0.8 + mix01(`${id}:skr`) * 0.5),
    });
  }
  let distant: SystemMakeup['distant'] = null;
  if (distantKind) {
    const az = mix01(`${id}:dsa`) * Math.PI * 2, el = (mix01(`${id}:dse`) - 0.5) * 0.9, dist = 3_000_000 + mix01(`${id}:dsd`) * 400_000; // beyond the sector's edge: a sun in the sky, never a place
    distant = { ...star(distantKind, 'far'), pos: [Math.cos(az) * Math.cos(el) * dist, Math.sin(el) * dist, Math.sin(az) * Math.cos(el) * dist] };
  }
  const mk: SystemMakeup = {
    systemId: id, showpiece: sp?.key ?? '', age, footprintM: sec.star.radius, config,
    primary: star(primaryKind, 'a'), companion: companionKind ? star(companionKind, 'b') : null, distant,
    planets, fields, features, sky,
    ...(sp && sp.nebula !== undefined ? { skyNebula: sp.nebula } : skyKind === 'embedded_cluster' ? { skyNebula: 'emission' as const } : {}),
  };
  memo.set(id, mk);
  return mk;
}
function norm(v: V3): V3 { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; }

/** A short name for a star kind (the chart, the HUD). */
export const STAR_LABEL: Readonly<Record<StarKind, string>> = {
  m_dwarf: 'red dwarf', k_dwarf: 'orange dwarf', g_dwarf: 'yellow dwarf', f_dwarf: 'yellow-white star', a_star: 'white star', ob_star: 'blue giant star',
  red_giant: 'red giant', agb: 'dying giant', post_agb: 'shrouded dying star', carbon_star: 'carbon star', mira: 'pulsing red giant', cepheid: 'pulsing supergiant',
  red_supergiant: 'red supergiant', blue_supergiant: 'blue supergiant', lbv: 'unstable blue giant', wolf_rayet: 'Wolf–Rayet star', be_star: 'spinning blue star',
  protostar: 'protostar', t_tauri: 'infant star', herbig: 'young white star',
  brown_dwarf: 'brown dwarf', white_dwarf: 'white dwarf', neutron_star: 'neutron star', pulsar: 'pulsar', magnetar: 'magnetar', black_hole: 'black hole',
};
/** One line for what a system is ("orange dwarf", "red giant with a white dwarf", "pulsar"). */
/**
 * What a showpiece IS, where the stars' own names do not say it: "orange dwarf with a white dwarf" is true of a
 * cataclysmic variable and tells a pilot nothing about the disc and the outbursts they are looking at. The galactic
 * chart's search matches these words too ("cataclysmic", "pulsar wind").
 */
export const SHOWPIECE_NOTE: Readonly<Record<string, string>> = {
  wolf_rayet_pinwheel: 'colliding-wind pinwheel', xray_binary_bh: 'black-hole X-ray binary', xray_binary_ns: 'X-ray binary',
  pulsar_nebula: 'in its wind nebula', pulsar_runaway: 'a runaway, its bow shock ahead', symbiotic: 'symbiotic pair', cataclysmic: 'cataclysmic variable',
  be_xray: 'Be X-ray binary', wd_debris: 'ringed with debris', young_binary: 'each with its own disc',
  detached_binary: 'eclipsing pair', hierarchical_triple: 'triple star', flare_star: 'flare star',
};

export function systemStarLabel(mk: SystemMakeup): string {
  const a = STAR_LABEL[mk.primary.kind];
  const an = (w: string): string => (/^[aeiou]/i.test(w) ? `an ${w}` : `a ${w}`);
  const base = mk.config === 'contact_binary' ? `contact pair of ${a}s`
    : mk.companion ? (mk.companion.kind === mk.primary.kind ? `pair of ${a}s` : `${a} with ${an(STAR_LABEL[mk.companion.kind])}`)
    : mk.distant ? `${a}, a distant ${STAR_LABEL[mk.distant.kind]}`
    : a;
  const note = mk.showpiece ? SHOWPIECE_NOTE[mk.showpiece] : undefined;
  return note ? `${base} — ${note}` : base;
}
